import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Link, useHistory, useParams } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";
import * as Yup from "yup";
import SkeletonLoaderBasic from "../../loaders/SkeletonLoaderBasic";
import Button from "../common/Button/Button";
import FormLabel from "../common/forms/FormLabel";
import FormInput from "../common/forms/FormInput";
import HtmlInputEditor from "../common/HtmlInputEditor";
import { RenderSchool } from "../../utils/CommonGroupingItem";
import { RenderCareerProgrammeStatus } from "./careerLibraryRenderers";
import {
    invalidateCareerLibraryQueries,
    useCareer,
    useCareerListFilters,
    useLinkableProgrammes,
} from "../../query/hooks/QueryHooksCareerLibrary";
import CommonCourseComponents, {
    qualificationLabel,
} from "../courses/CommonCourseComponents";
import PermissionsGate from "../../utils/permissionGate";
import LinkedItemsTable from "./LinkedItemsTable";
import LinkPicker from "./LinkPicker";
import RowActions from "./RowActions";
import {
    cardStyle,
    careerSelectStyles,
    FieldGrid,
    subHeaderStyle,
    tagSelectStyles,
} from "./careerLibrary.styles";
import {
    UpdateCareer,
    CreateCareerTag,
    DeleteCareerTag,
} from "../../services/CareerLibraryServices";

//TODO: This should be DB driven
const demandLevels = ["High", "Medium", "Low"];
const workEnvironments = ["Remote", "Hybrid", "In Office"];
const publishStatuses = ["Published", "Draft", "Unpublished"];
const nqfLevels = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

const tagGroups = [
    { type: "skill", label: "Skill Tags" },
    { type: "career", label: "Career Tags" },
    { type: "interest", label: "Interest Tags" },
];

const validationSchema = Yup.object({
    name: Yup.string()
        .required("Name is required")
        .max(150, "Max 150 characters")
        .trim(),
    school_id: Yup.number().nullable().required("School is required"),
    salary_entry: Yup.number()
        .typeError("Salary must be a number")
        .required("Salary Entry Level is required")
        .min(0, "Salary cannot be negative"),
    salary_expert: Yup.number()
        .typeError("Salary must be a number")
        .required("Salary Expert Level is required")
        .min(0, "Salary cannot be negative"),
    demand_level: Yup.string().required("Demand Level is required"),
    nqf_level: Yup.string().required("NQF Level is required"),
    work_environment: Yup.string().required("Work Environment is required"),
    short_description: Yup.string()
        .nullable()
        .max(500, "Summary cannot be longer than 500 characters"),
    publish_status: Yup.string().required("Status is required"),
    skillTagIds: Yup.array().min(1, "At least one Skill Tag is required"),
    careerTagIds: Yup.array().min(1, "At least one Career Tag is required"),
    interestTagIds: Yup.array().min(1, "At least one Interest Tag is required"),
});

const toOptions = (values) => values.map((value) => ({ value, label: value }));

const programmeKey = (programme) => `${programme.pmc_type}:${programme.pmc_id}`;

const programmeRoute = (programme) =>
    `/program-management-center/programmes/${
        programme.pmc_type === "shortcourse" ? "shortCourse" : "programme"
    }/open/${programme.pmc_id}`;

function CareerDetails() {
    const { id } = useParams();
    const history = useHistory();
    const [saving, setSaving] = useState(false);
    const [description, setDescription] = useState("");
    const [programmePickerOpen, setProgrammePickerOpen] = useState(false);

    const careerQuery = useCareer(id);
    const filtersQuery = useCareerListFilters();
    const linkableQuery = useLinkableProgrammes();

    const career = careerQuery.data ?? null;
    const filterOptions = filtersQuery.data ?? {};
    const linkablePrograms = linkableQuery.data ?? [];

    useEffect(() => {
        setDescription(career?.description || "");
    }, [career]);

    useEffect(() => {
        if (careerQuery.isError) {
            Swal.fire("Error", "This career could not be loaded.", "error");
        }
    }, [careerQuery.isError]);

    const careerTags = career?.tags || [];

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: career?.name || "",
            school_id: career?.school_id || null,
            salary_entry: career?.salary_entry ?? "",
            salary_expert: career?.salary_expert ?? "",
            demand_level: career?.demand_level || "",
            nqf_level: career?.nqf_level || "",
            work_environment: career?.work_environment || "",
            short_description: career?.short_description || "",
            publish_status: career?.publish_status || "Draft",
            skillTagIds: careerTags
                .filter((tag) => tag.tag_type === "skill")
                .map((tag) => tag.id),
            careerTagIds: careerTags
                .filter((tag) => tag.tag_type === "career")
                .map((tag) => tag.id),
            interestTagIds: careerTags
                .filter((tag) => tag.tag_type === "interest")
                .map((tag) => tag.id),
            programmes: (career?.programmes || []).map((programme) => ({
                pmc_id: programme.pmc_id,
                pmc_type: programme.pmc_type,
            })),
        },
        validationSchema,
        onSubmit: async (values) => {
            setSaving(true);
            try {
                const payload = {
                    ...values,
                    description,
                    salary_entry:
                        values.salary_entry === ""
                            ? null
                            : Number(values.salary_entry),
                    salary_expert:
                        values.salary_expert === ""
                            ? null
                            : Number(values.salary_expert),
                    tagIds: [
                        ...values.skillTagIds,
                        ...values.careerTagIds,
                        ...values.interestTagIds,
                    ],
                };
                if (id) {
                    payload.id = Number(id);
                }

                const saveResponse = await UpdateCareer(payload);
                if (saveResponse.status === 200) {
                    await invalidateCareerLibraryQueries();
                    Swal.fire(
                        "Saved",
                        saveResponse.data?.message || "Career saved.",
                        "success",
                    );
                    const savedId = saveResponse.data?.data?.id;
                    if (!id && savedId) {
                        history.push(`/career-library/careers/open/${savedId}`);
                    }
                }
            } catch (error) {
                console.log("CareerDetails.js->onSubmit->Error", error);
                Swal.fire("Error", "The career could not be saved.", "error");
            } finally {
                setSaving(false);
            }
        },
    });

    /**
     * Adds a tag an editor typed into the shared vocabulary and selects it.
     * @param {string} tagName New tag name
     * @param {string} tagType Tag group being added to
     * @param {string} fieldName Formik field holding that group's ids
     */
    const handleCreateTag = async (tagName, tagType, fieldName) => {
        try {
            const tagResponse = await CreateCareerTag({
                name: tagName,
                tag_type: tagType,
            });
            const createdTag = tagResponse.data?.data;
            if (!createdTag) {
                return;
            }

            await invalidateCareerLibraryQueries();
            formik.setFieldValue(fieldName, [
                ...formik.values[fieldName],
                createdTag.id,
            ]);
        } catch (error) {
            console.log("CareerDetails.js->handleCreateTag->Error", error);
            Swal.fire("Error", "The tag could not be added.", "error");
        }
    };

    /**
     * Retires a tag from the shared vocabulary, so no career can pick it again.
     * @param {object} tag The tag being removed
     * @param {string} fieldName Formik field holding that group's ids
     */
    const handleDeleteTag = async (tag, fieldName) => {
        try {
            const swalResult = await Swal.fire({
                title: `Delete "${tag.label}"?`,
                text: tag.career_count
                    ? `This tag is used by ${tag.career_count} career(s) and will be removed from them.`
                    : "This tag will no longer be available to any career.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "var(--button-color)",
                cancelButtonColor: "var(--states-alert)",
                confirmButtonText: "Yes, delete it!",
            });

            if (!swalResult.isConfirmed) {
                return;
            }

            const deleteResponse = await DeleteCareerTag(tag.value);
            if (deleteResponse.status === 200) {
                formik.setFieldValue(
                    fieldName,
                    formik.values[fieldName].filter(
                        (tagId) => tagId !== tag.value,
                    ),
                );
                await invalidateCareerLibraryQueries();
            }
        } catch (error) {
            console.log("CareerDetails.js->handleDeleteTag->Error", error);
            Swal.fire("Error", "The tag could not be deleted.", "error");
        }
    };

    /**
     * Enter on a typed value not already in the vocabulary creates the tag.
     * @param {object} event Keyboard event from the tag select
     * @param {string} tagType Tag group being added to
     * @param {string} fieldName Formik field holding that group's ids
     */
    const handleTagKeyDown = (event, tagType, fieldName) => {
        if (event.key !== "Enter") {
            return;
        }
        const typedName = event.target.value?.trim();
        if (!typedName) {
            return;
        }
        const existing = (filterOptions.TagFilter || []).find(
            (tag) =>
                tag.tag_type === tagType &&
                tag.label.toLowerCase() === typedName.toLowerCase(),
        );
        if (existing) {
            return;
        }
        event.preventDefault();
        handleCreateTag(typedName, tagType, fieldName);
    };

    if (
        (id && careerQuery.isLoading) ||
        filtersQuery.isLoading ||
        linkableQuery.isLoading
    ) {
        return <SkeletonLoaderBasic />;
    }

    const schoolOptions = filterOptions.SchoolOptions || [];
    const tagOptions = filterOptions.TagFilter || [];

    const linkedKeys = formik.values.programmes.map(programmeKey);
    const linkedProgrammeRows = linkablePrograms.filter((linkable) =>
        linkedKeys.includes(programmeKey(linkable)),
    );
    /**
     * Distinct filter options for one column of the linked-programmes table.
     * @param {Function} readValue Pulls the column value off a row
     * @returns Option list
     */
    const distinctOptions = (sourceRows, readValue) => {
        const values = sourceRows
            .map(readValue)
            .filter(
                (value) =>
                    value !== null && value !== undefined && value !== "",
            );
        return [...new Set(values)].map((value) => ({
            value,
            label: String(value),
        }));
    };

    /**
     * Filters for one programme table. The linked table offers what it lists; the
     * assign dialog offers everything linkable, not just the linked rows.
     * @param {Array<object>} sourceRows Rows the options are drawn from
     * @returns Filter definitions
     */
    const buildProgrammeFilters = (sourceRows) => [
        {
            key: "status",
            label: "Status",
            options: distinctOptions(sourceRows, (row) => row.status),
            renderLabelFunction: RenderCareerProgrammeStatus,
            readValue: (row) => row.status,
        },
        {
            key: "type",
            label: "Type",
            options: distinctOptions(
                sourceRows,
                (row) => row.qualification_type,
            ),
            renderLabelFunction: (value) => ({
                html: <CommonCourseComponents degree={value} />,
            }),
            readValue: (row) => row.qualification_type,
        },
        {
            key: "school",
            label: "School",
            options: distinctOptions(sourceRows, (row) => row.school),
            renderLabelFunction: RenderSchool,
            readValue: (row) => row.school,
        },
        {
            key: "nqf",
            label: "NQF Level",
            options: distinctOptions(sourceRows, (row) => row.nqf_level),
            readValue: (row) => row.nqf_level,
        },
    ];

    const unlinkProgrammes = (keys) =>
        formik.setFieldValue(
            "programmes",
            formik.values.programmes.filter(
                (programme) => !keys.includes(programmeKey(programme)),
            ),
        );

    const programmeColumns = [
        {
            name: "Programme Details",
            selector: "name",
            sortable: true,
            grow: 2,
            minWidth: "250px",
            cell: (row) => (
                <Link
                    to={programmeRoute(row)}
                    title={row.name}
                    className="career-name-link curser feature-name"
                >
                    <span className="textLimit100">{row.name}</span>
                </Link>
            ),
        },
        {
            name: "Status",
            selector: "status",
            sortable: true,
            width: "100px",
            cell: (row) =>
                row?.status != null
                    ? RenderCareerProgrammeStatus(row.status).html
                    : "-",
        },
        {
            name: "Type",
            selector: "qualification_type",
            sortable: true,
            width: "170px",
            cell: (row) =>
                row?.qualification_type != null ? (
                    <CommonCourseComponents
                        degree={row.qualification_type}
                        icon={row.qualification_icon}
                        hue={row.qualification_hue}
                    />
                ) : (
                    "-"
                ),
        },
        {
            name: "School",
            selector: "school",
            sortable: true,
            grow: 1.5,
            minWidth: "240px",
            cell: (row) =>
                row?.school ? (
                    <span className="feature-name">
                        <span className="textLimit100">
                            {RenderSchool(row.school).html}
                        </span>
                    </span>
                ) : (
                    "-"
                ),
        },
        {
            name: "NQF Level",
            selector: "nqf_level",
            sortable: true,
            width: "160px",
            cell: (row) => (
                <div title={row?.nqf_level}>{row?.nqf_level || "-"}</div>
            ),
        },
        {
            name: "Action",
            width: "96px",
            right: true,
            cell: (row) => (
                <RowActions
                    onOpen={() => history.push(programmeRoute(row))}
                    openTitle="Open Programme"
                    openScopes={["pmcpview"]}
                    onDanger={() => unlinkProgrammes([programmeKey(row)])}
                    dangerTitle="Unlink Programme"
                    dangerIcon="fal fa-unlink"
                    dangerScopes={["careerlibrarycareersedit"]}
                />
            ),
        },
    ];

    const programmeFilters = buildProgrammeFilters(linkedProgrammeRows);
    const pickerFilters = buildProgrammeFilters(linkablePrograms);

    return (
        <div
            className="card-profile-info-card career-detail-form"
            style={{ border: "none", background: "transparent" }}
        >
            <div
                className="card-body"
                style={{ background: "transparent", padding: 0 }}
            >
                <form onSubmit={formik.handleSubmit}>
                    <div
                        className="card mb-4 career-information-card"
                        style={cardStyle}
                    >
                        <h4 style={subHeaderStyle}>Career Information</h4>

                        <FieldGrid>
                            <div>
                                <div className="form-group mb-0">
                                    <FormLabel htmlFor="name" required>
                                        Name
                                    </FormLabel>
                                    <FormInput
                                        id="name"
                                        name="name"
                                        placeholder="Type here"
                                        value={formik.values.name}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.errors.name}
                                        touched={formik.touched.name}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="form-group mb-0">
                                    <FormLabel required>Status</FormLabel>
                                    <Select
                                        name="publish_status"
                                        value={
                                            toOptions(publishStatuses).find(
                                                (status) =>
                                                    status.value ===
                                                    formik.values
                                                        .publish_status,
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                            formik.setFieldValue(
                                                "publish_status",
                                                selected ? selected.value : "",
                                            )
                                        }
                                        options={toOptions(publishStatuses)}
                                        placeholder="Type Here.."
                                        styles={careerSelectStyles}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="form-group mb-0">
                                    <FormLabel required>School</FormLabel>
                                    <Select
                                        name="school_id"
                                        value={
                                            schoolOptions.find(
                                                (school) =>
                                                    school.value ===
                                                    formik.values.school_id,
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                            formik.setFieldValue(
                                                "school_id",
                                                selected
                                                    ? selected.value
                                                    : null,
                                            )
                                        }
                                        onBlur={() =>
                                            formik.setFieldTouched(
                                                "school_id",
                                                true,
                                            )
                                        }
                                        options={schoolOptions}
                                        isClearable
                                        placeholder="Type Here.."
                                        styles={careerSelectStyles}
                                    />
                                    {formik.errors.school_id &&
                                    formik.touched.school_id ? (
                                        <div className="invalid-feedback d-block">
                                            {formik.errors.school_id}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            <div>
                                <div className="form-group mb-0">
                                    <FormLabel required>NQF Level</FormLabel>
                                    <Select
                                        name="nqf_level"
                                        value={
                                            toOptions(nqfLevels).find(
                                                (nqfLevel) =>
                                                    nqfLevel.value ===
                                                    formik.values.nqf_level,
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                            formik.setFieldValue(
                                                "nqf_level",
                                                selected ? selected.value : "",
                                            )
                                        }
                                        onBlur={() =>
                                            formik.setFieldTouched(
                                                "nqf_level",
                                                true,
                                            )
                                        }
                                        options={toOptions(nqfLevels)}
                                        isClearable
                                        placeholder="Type Here.."
                                        styles={careerSelectStyles}
                                    />
                                    {formik.errors.nqf_level &&
                                    formik.touched.nqf_level ? (
                                        <div className="invalid-feedback d-block">
                                            {formik.errors.nqf_level}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            <div>
                                <div className="form-group mb-0">
                                    <FormLabel htmlFor="salary_entry" required>
                                        Salary Entry Level (Monthly)
                                    </FormLabel>
                                    <FormInput
                                        id="salary_entry"
                                        name="salary_entry"
                                        type="number"
                                        min="0"
                                        prefix="R"
                                        placeholder="100"
                                        value={formik.values.salary_entry}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.errors.salary_entry}
                                        touched={formik.touched.salary_entry}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="form-group mb-0">
                                    <FormLabel htmlFor="salary_expert" required>
                                        Salary Expert Level (Monthly)
                                    </FormLabel>
                                    <FormInput
                                        id="salary_expert"
                                        name="salary_expert"
                                        type="number"
                                        min="0"
                                        prefix="R"
                                        placeholder="100"
                                        value={formik.values.salary_expert}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.errors.salary_expert}
                                        touched={formik.touched.salary_expert}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className="form-group mb-0">
                                    <FormLabel required>Demand Level</FormLabel>
                                    <Select
                                        name="demand_level"
                                        value={
                                            toOptions(demandLevels).find(
                                                (demandLevel) =>
                                                    demandLevel.value ===
                                                    formik.values.demand_level,
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                            formik.setFieldValue(
                                                "demand_level",
                                                selected ? selected.value : "",
                                            )
                                        }
                                        onBlur={() =>
                                            formik.setFieldTouched(
                                                "demand_level",
                                                true,
                                            )
                                        }
                                        options={toOptions(demandLevels)}
                                        isClearable
                                        placeholder="Type Here.."
                                        styles={careerSelectStyles}
                                    />
                                    {formik.errors.demand_level &&
                                    formik.touched.demand_level ? (
                                        <div className="invalid-feedback d-block">
                                            {formik.errors.demand_level}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            <div>
                                <div className="form-group mb-0">
                                    <FormLabel required>
                                        Work Environment
                                    </FormLabel>
                                    <Select
                                        name="work_environment"
                                        value={
                                            toOptions(workEnvironments).find(
                                                (workEnvironment) =>
                                                    workEnvironment.value ===
                                                    formik.values
                                                        .work_environment,
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                            formik.setFieldValue(
                                                "work_environment",
                                                selected ? selected.value : "",
                                            )
                                        }
                                        onBlur={() =>
                                            formik.setFieldTouched(
                                                "work_environment",
                                                true,
                                            )
                                        }
                                        options={toOptions(workEnvironments)}
                                        isClearable
                                        placeholder="Type Here.."
                                        styles={careerSelectStyles}
                                    />
                                    {formik.errors.work_environment &&
                                    formik.touched.work_environment ? (
                                        <div className="invalid-feedback d-block">
                                            {formik.errors.work_environment}
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </FieldGrid>

                        <FieldGrid>
                            <div className="career-field--full">
                                <div className="form-group mb-0">
                                    <FormLabel htmlFor="description">
                                        Description
                                    </FormLabel>
                                    <HtmlInputEditor
                                        editorState={description}
                                        setEditorState={setDescription}
                                        hideSign={true}
                                        belowNoMargin={true}
                                        isCKEditor={true}
                                        initialValue={career?.description || ""}
                                    />
                                </div>
                            </div>

                            <div className="career-field--full">
                                <div className="form-group mb-0">
                                    <FormLabel htmlFor="short_description">
                                        Summary
                                    </FormLabel>
                                    <FormInput
                                        id="short_description"
                                        name="short_description"
                                        maxLength={500}
                                        placeholder="The one line shown on the career's card on the website"
                                        value={formik.values.short_description}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.errors.short_description}
                                        touched={
                                            formik.touched.short_description
                                        }
                                    />
                                </div>
                            </div>

                            {tagGroups.map((tagGroup) => {
                                const fieldName = `${tagGroup.type}TagIds`;
                                const selectedIds = formik.values[fieldName];
                                const groupOptions = tagOptions.filter(
                                    (tag) => tag.tag_type === tagGroup.type,
                                );
                                const selectedTags = groupOptions.filter(
                                    (option) =>
                                        selectedIds.includes(option.value),
                                );

                                return (
                                    <div
                                        className="career-field--full"
                                        key={tagGroup.type}
                                    >
                                        <div className="form-group mb-0">
                                            <FormLabel required>
                                                {tagGroup.label}
                                            </FormLabel>
                                            <Select
                                                isMulti
                                                name={`tags-${tagGroup.type}`}
                                                value={selectedTags}
                                                onChange={(selected) =>
                                                    formik.setFieldValue(
                                                        fieldName,
                                                        (selected || []).map(
                                                            (option) =>
                                                                option.value,
                                                        ),
                                                    )
                                                }
                                                onKeyDown={(event) =>
                                                    handleTagKeyDown(
                                                        event,
                                                        tagGroup.type,
                                                        fieldName,
                                                    )
                                                }
                                                onBlur={() =>
                                                    formik.setFieldTouched(
                                                        fieldName,
                                                        true,
                                                    )
                                                }
                                                options={groupOptions}
                                                placeholder="Type Here.."
                                                formatOptionLabel={(
                                                    option,
                                                    { context },
                                                ) =>
                                                    context === "menu" ? (
                                                        <span className="career-tag-option">
                                                            <span>
                                                                {option.label} (
                                                                {option.career_count ||
                                                                    0}
                                                                )
                                                            </span>
                                                            <i
                                                                className="fal fa-trash-alt career-tag-option__delete"
                                                                title={`Delete ${option.label}`}
                                                                onMouseDown={(
                                                                    event,
                                                                ) => {
                                                                    event.preventDefault();
                                                                    event.stopPropagation();
                                                                    handleDeleteTag(
                                                                        option,
                                                                        fieldName,
                                                                    );
                                                                }}
                                                            />
                                                        </span>
                                                    ) : (
                                                        option.label
                                                    )
                                                }
                                                noOptionsMessage={({
                                                    inputValue,
                                                }) =>
                                                    inputValue.trim()
                                                        ? `Press Enter to add "${inputValue.trim()}"`
                                                        : "Start typing to add a tag"
                                                }
                                                styles={tagSelectStyles}
                                            />
                                            {formik.errors[fieldName] &&
                                            formik.touched[fieldName] ? (
                                                <div className="invalid-feedback d-block">
                                                    {formik.errors[fieldName]}
                                                </div>
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            })}
                        </FieldGrid>
                    </div>

                    <LinkedItemsTable
                        rows={linkedProgrammeRows}
                        columns={programmeColumns}
                        searchText={(row) => `${row.name} ${row.school || ""}`}
                        exportFileName="Linked_Programmes"
                        exportRow={(fileType, exportRows) =>
                            exportRows.map((row) => ({
                                "Programme Details": row.name,
                                Status: row.status
                                    ? RenderCareerProgrammeStatus(row.status)
                                          .text
                                    : "-",
                                Type: qualificationLabel(
                                    row.qualification_type,
                                ),
                                School: row.school
                                    ? RenderSchool(row.school).text
                                    : "-",
                                "NQF Level": row.nqf_level || "-",
                            }))
                        }
                        filters={programmeFilters}
                        linkButton={
                            <LinkPicker
                                key="link-programme"
                                label="Link Programme"
                                title="Assign Linked Programmes"
                                rows={linkablePrograms}
                                linkedKeys={linkedKeys}
                                rowKey={programmeKey}
                                columns={programmeColumns.filter(
                                    (column) => column.name !== "Action",
                                )}
                                searchText={(row) =>
                                    `${row.name} ${row.school || ""}`
                                }
                                filters={pickerFilters}
                                exportFileName="Programmes"
                                open={programmePickerOpen}
                                onOpenChange={setProgrammePickerOpen}
                                exportRow={(fileType, exportRows) =>
                                    exportRows.map((row) => ({
                                        "Programme Details": row.name,
                                        Status: row.status
                                            ? RenderCareerProgrammeStatus(
                                                  row.status,
                                              ).text
                                            : "-",
                                        Type: qualificationLabel(
                                            row.qualification_type,
                                        ),
                                        School: row.school
                                            ? RenderSchool(row.school).text
                                            : "-",
                                        "NQF Level": row.nqf_level || "-",
                                    }))
                                }
                                onToggle={(key, isLinked) => {
                                    if (isLinked) {
                                        unlinkProgrammes([key]);
                                        return;
                                    }

                                    const programme = linkablePrograms.find(
                                        (row) => programmeKey(row) === key,
                                    );
                                    if (programme) {
                                        formik.setFieldValue("programmes", [
                                            ...formik.values.programmes,
                                            {
                                                pmc_id: programme.pmc_id,
                                                pmc_type: programme.pmc_type,
                                            },
                                        ]);
                                    }
                                }}
                            />
                        }
                    />

                    {!programmePickerOpen && (
                        <div className="sticky-save-footer form-group-save-cancel">
                            <PermissionsGate
                                scopes={[
                                    id
                                        ? "careerlibrarycareersedit"
                                        : "careerlibrarycareersadd",
                                ]}
                                errorProps={{ disabled: true }}
                            >
                                <Button
                                    type="submit"
                                    variant="primary"
                                    isLoading={saving}
                                    iconLeft={<i className="fal fa-save"></i>}
                                >
                                    Save
                                </Button>
                            </PermissionsGate>
                            <Button
                                type="button"
                                variant="danger"
                                onClick={() =>
                                    history.push(
                                        "/career-library/careers/table",
                                    )
                                }
                                iconLeft={<i className="fal fa-times"></i>}
                            >
                                Cancel
                            </Button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}

export default CareerDetails;
